import React from 'react';
import { FileText, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import type { ClientHealthMetrics } from '@/types/client';
import { useFetchClientInvoices } from '@/hooks/useClients';

interface FinancialTabProps {
  clientId: string;
  healthMetrics: ClientHealthMetrics | null;
}

export const FinancialTab: React.FC<FinancialTabProps> = ({ clientId, healthMetrics }) => {
  const { data: invoices = [], isLoading, error } = useFetchClientInvoices(clientId);
  const m = healthMetrics;

  return (
    <div className="space-y-4">
      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <SummaryCard
          label="Total Revenue"
          value={m?.total_revenue != null ? `$${m.total_revenue.toLocaleString('en-US', { maximumFractionDigits: 0 })}` : '--'}
          colorClass="text-primary"
        />
        <SummaryCard
          label="Avg Days to Pay"
          value={m?.avg_days_to_payment != null ? `${Math.round(m.avg_days_to_payment)}` : '--'}
          subValue="days"
        />
        <SummaryCard
          label="Cancel Rate"
          value={m?.cancellation_rate != null ? `${m.cancellation_rate.toFixed(0)}%` : '--'}
          colorClass={m?.cancellation_rate != null && m.cancellation_rate > 20 ? 'text-led-red' : undefined}
        />
        <SummaryCard
          label="Invoices"
          value={`${invoices.length}`}
        />
      </div>

      {/* Invoice list */}
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
        </div>
      ) : error ? (
        <p className="text-sm text-destructive">Failed to load invoices.</p>
      ) : invoices.length === 0 ? (
        <div className="text-center py-8 space-y-1">
          <FileText className="h-8 w-8 text-muted-foreground/40 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">No invoices yet.</p>
          <p className="text-xs text-muted-foreground">Invoices will appear here after bookings are billed.</p>
        </div>
      ) : (
        <div className="space-y-1">
          {invoices.map((inv) => (
            <div
              key={inv.id}
              className="flex items-center gap-3 bg-surface-1 border border-border rounded px-3 py-2.5"
            >
              <FileText className="h-4 w-4 text-led-purple flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm text-foreground">
                    {inv.total_amount != null ? `$${inv.total_amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '--'}
                  </span>
                  {inv.status_id && (
                    <span className="text-[10px] font-label uppercase tracking-wider px-1.5 py-0.5 rounded text-muted-foreground bg-surface-3/50">
                      Invoice
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {inv.invoice_date
                    ? `Issued ${format(new Date(inv.invoice_date), 'MMM d, yyyy')}`
                    : 'No issue date'}
                  {inv.due_date && ` — Due ${format(new Date(inv.due_date), 'MMM d, yyyy')}`}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

function SummaryCard({
  label,
  value,
  subValue,
  colorClass,
}: {
  label: string;
  value: string;
  subValue?: string;
  colorClass?: string;
}) {
  return (
    <div className="bg-surface-2 border border-border rounded p-3 text-center">
      <span className="text-[10px] font-label uppercase tracking-wider text-muted-foreground block mb-1">
        {label}
      </span>
      <span className={cn('font-mono text-lg font-medium', colorClass || 'text-foreground')}>
        {value}
      </span>
      {subValue && (
        <span className="text-[10px] font-label text-muted-foreground block mt-0.5">{subValue}</span>
      )}
    </div>
  );
}
